import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Theme, Tooltip, Typography } from "@mui/material";
import { Check, ChevronRight, CheckCircleOutline, Login, Logout } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { signOut } from "aws-amplify/auth";
import { RootState } from "@store/store";
import useInterfaceConfig from "@hooks/useInterfaceConfigV2";
import useIsAuthenticated from "@hooks/useIsAuthenticated";
import { getStaticResourceUrl } from "@helpers/funciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";
import { DialogConfirm } from "./DialogConfirm";
import {
  SIDEBAR_LOGO_HEIGHT,
  SIDEBAR_WIDTH,
  getCompanyDisplayName,
  getInitials,
  getSidebarColors,
  loginButtonSx,
  useTofuRail,
  white,
} from "./tofuSidebar.helpers";

/**
 * Por ahora el rail solo muestra las tres etapas (Onboarding, Expediente, Firma). Las fases de cada
 * etapa (Cotización, Cuenta, Empresa…) quedan apagadas; para volver a mostrarlas, cambiar a `true`.
 * La configuración de fases sigue en uso: es la que decide en qué etapa va el usuario.
 */
const SHOW_RAIL_PHASES = false;

/** Fila "Guardado automáticamente": apagada hasta que se implemente el autoguardado. */
const SHOW_AUTOSAVE_ROW = false;

export const TofuSidebarContent = () => {
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();
  const isAuthenticated = useIsAuthenticated();
  const company = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const personalData = useSelector((state: RootState) => state.register.personal_data);

  const userName = [personalData.name, personalData.first_lastname]
    .filter((part) => part?.trim())
    .join(" ")
    .trim();
  const userEmail = accountUser.email?.trim();

  // Mismo cierre de sesión que el Navbar legacy: el canal avisa a las demás pestañas abiertas.
  // Cerrar sesión pide confirmación antes (`DialogConfirm`).
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      sessionStorage.clear();
      const channel = new BroadcastChannel("session");
      channel.postMessage("logout");
      channel.close();
      navigate(PATHS_AUTH_V2.login.path);
    } finally {
      setIsSigningOut(false);
      setConfirmSignOut(false);
    }
  };

  // Mismo criterio que Navbar: el logo del tenant vive en los recursos estáticos y se oculta
  // hasta que carga, para no dejar el ícono roto mientras llega. Sobre el fondo oscuro se usa la
  // versión para fondo oscuro que manda la config (`lateral.logo_bg_dark`), si existe.
  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const logo = getStaticResourceUrl(interfaceConfig?.lateral?.logo_bg_dark || interfaceConfig?.navbar.logo);

  const companyName = getCompanyDisplayName(company);
  const companyRfc = company.rfc?.trim();

  const { groups, activeGroupIndex, activeIndex, phases } = useTofuRail();

  return (
    <>
      {/* `.sb-logo` — padding 26px 22px 16px, con la línea inferior de `.sb-dark` */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pt: "26px",
          px: "22px",
          pb: "16px",
          mb: "4px",
          minHeight: 22,
          borderBottom: `1px solid ${white(0.07)}`,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="logo"
          onLoad={() => setIsLogoLoading(false)}
          sx={{
            // Alto fijo para todos los tenants: las medidas `navbar.logo_*` son las del navbar legacy
            // y en el sidebar se veían desproporcionadas. El ancho sigue la relación de aspecto y
            // `maxWidth` evita que un logo ancho desborde el sidebar.
            height: SIDEBAR_LOGO_HEIGHT,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: isLogoLoading ? "none" : "block",
          }}
        />
      </Box>

      {/* `.sb-org` — solo con sesión y cuando ya hay una empresa/solicitud en el store. */}
      {isAuthenticated && companyName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            mx: "16px",
            mt: "18px",
            mb: "22px",
            p: "11px 12px",
            bgcolor: white(0.06),
            border: `1px solid ${white(0.1)}`,
            borderRadius: "10px",
            cursor: "pointer",
            transition: "background .12s, border-color .12s",
            "&:hover": { bgcolor: white(0.1), borderColor: white(0.18) },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: (theme) => getSidebarColors(theme).avatar,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 11, letterSpacing: "0.02em" }}>
              {getInitials(companyName)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: "#fff", letterSpacing: "-0.01em" }}>
              {companyName}
            </Typography>
            {companyRfc && (
              <Typography sx={{ fontSize: 11, color: white(0.45), fontVariantNumeric: "tabular-nums" }}>
                {companyRfc}
              </Typography>
            )}
          </Box>
          <ChevronRight sx={{ fontSize: 16, color: white(0.28), flexShrink: 0 }} />
        </Box>
      )}

      {/* `.sb-rail` — padding 4px 22px 0, gap 26px */}
      <Box
        sx={{
          flex: 1,
          pt: isAuthenticated && companyName ? "4px" : "22px",
          px: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "26px",
        }}
      >
        {groups.map((g, i) => {
          const groupActive = i === activeGroupIndex;
          const groupDone = i < activeGroupIndex;
          return (
            <Box key={g.key}>
              {/* `.sb-rail-group-h` — padding 4px 0 14px, uppercase 11px/600, ls .08em */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pt: "4px", pb: "14px" }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    border: "1px solid",
                    ...(groupDone || groupActive
                      ? {
                          bgcolor: (theme) => getSidebarColors(theme).mark,
                          borderColor: (theme) => getSidebarColors(theme).mark,
                          color: "#fff",
                        }
                      : { bgcolor: white(0.08), borderColor: white(0.12), color: white(0.38) }),
                    boxShadow: groupActive ? (theme) => `0 0 0 3px ${getSidebarColors(theme).markRing}` : "none",
                  }}
                >
                  {groupDone ? (
                    <Check sx={{ fontSize: 10 }} />
                  ) : (
                    <Typography component="span" sx={{ fontSize: 10, lineHeight: 1, color: "inherit" }}>
                      {g.num}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: groupActive ? white(0.92) : groupDone ? white(0.48) : white(0.32),
                  }}
                >
                  {g.label}
                </Typography>
              </Box>

              {/* `.sb-rail-list` — pl 8px, línea conectora en left 11px. Apagado por `SHOW_RAIL_PHASES`. */}
              {SHOW_RAIL_PHASES && groupActive && (
                <Box
                  sx={{
                    position: "relative",
                    pl: "8px",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: "11px",
                      top: "16px",
                      bottom: "16px",
                      width: "1px",
                      bgcolor: white(0.09),
                    },
                  }}
                >
                  {phases.map((p, phaseIdx) => {
                    const done = phaseIdx < activeIndex;
                    const active = phaseIdx === activeIndex;
                    return (
                      /* `.sb-rail-item` — py 9px, pl 4px, pr 8px, ml -4px, radius 6px */
                      <Box
                        key={p.order}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          py: "9px",
                          pl: "4px",
                          pr: "8px",
                          ml: "-4px",
                          borderRadius: "6px",
                          position: "relative",
                          transition: "color .15s ease, background .15s ease",
                          bgcolor: active ? (theme) => getSidebarColors(theme).activeBg : "transparent",
                        }}
                      >
                        {/* `.sb-rail-mark` — 16px, borde 1.5px, relleno al completar */}
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 1,
                            border: "1.5px solid",
                            ...(done && {
                              bgcolor: (theme: Theme) => getSidebarColors(theme).mark,
                              borderColor: (theme: Theme) => getSidebarColors(theme).mark,
                              color: "#fff",
                            }),
                            ...(active && {
                              bgcolor: (theme: Theme) => getSidebarColors(theme).markSoft,
                              borderColor: (theme: Theme) => getSidebarColors(theme).mark,
                            }),
                            ...(!done &&
                              !active && {
                                bgcolor: (theme: Theme) => getSidebarColors(theme).bg,
                                borderColor: white(0.18),
                              }),
                          }}
                        >
                          {done ? (
                            <Check sx={{ fontSize: 11 }} />
                          ) : (
                            /* `.sb-rail-dot` — 5px en pendiente, 7px + halo en activo */
                            <Box
                              sx={{
                                width: active ? 7 : 5,
                                height: active ? 7 : 5,
                                borderRadius: "50%",
                                bgcolor: active ? (theme) => getSidebarColors(theme).dot : white(0.3),
                                boxShadow: active ? (theme) => `0 0 0 3px ${getSidebarColors(theme).markRing}` : "none",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            letterSpacing: "-0.005em",
                            color: active ? "#fff" : done ? white(0.42) : white(0.38),
                            fontWeight: active ? 600 : 500,
                          }}
                        >
                          {p.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Sin sesión (cotizador, crear cuenta) no hay nada guardado ni usuario que mostrar. */}
      {isAuthenticated && (
        <>
          {/* `.sb-saved-row` — padding 11px 22px, 11px. Apagado por `SHOW_AUTOSAVE_ROW`. */}
          {SHOW_AUTOSAVE_ROW && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                p: "11px 22px",
                borderTop: `1px solid ${white(0.08)}`,
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 14, color: (theme) => getSidebarColors(theme).dot }} />
              <Typography sx={{ fontSize: 11, color: white(0.28) }}>Guardado automáticamente</Typography>
            </Box>
          )}

          {/* `.sb-foot` — avatar 30px, nombre 12px/500, correo 11px */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              p: "14px",
              borderTop: `1px solid ${white(0.08)}`,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                bgcolor: white(0.1),
                color: white(0.72),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "inherit" }}>
                {getInitials(userName || userEmail || "")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
              {userName && (
                <Typography noWrap sx={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>
                  {userName}
                </Typography>
              )}
              {userEmail && (
                <Typography noWrap sx={{ fontSize: 11, color: white(0.38) }}>
                  {userEmail}
                </Typography>
              )}
            </Box>
            <Tooltip title="Cerrar sesión">
              <IconButton
                size="small"
                onClick={() => setConfirmSignOut(true)}
                data-testid="sidebar-logout-button"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  color: white(0.32),
                  "&:hover": { bgcolor: white(0.08), color: "#fff" },
                }}
              >
                <Logout sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Sin sesión: acceso al login en lugar del pie del usuario. */}
      {!isAuthenticated && (
        <Box sx={{ p: "14px", borderTop: `1px solid ${white(0.08)}` }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Login sx={{ fontSize: "16px !important" }} />}
            onClick={() => navigate(PATHS_AUTH_V2.login.path)}
            data-testid="sidebar-login-button"
            sx={[loginButtonSx, { height: 40 }]}
          >
            Iniciar sesión
          </Button>
        </Box>
      )}
      <DialogConfirm
        open={confirmSignOut}
        icon={<Logout />}
        title="¿Cerrar sesión?"
        message="Tu avance queda guardado. Para continuar con tu solicitud tendrás que volver a iniciar sesión."
        cancelLabel="Cancelar"
        confirmLabel="Sí, cerrar sesión"
        loadingLabel="Cerrando sesión…"
        loading={isSigningOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={handleSignOut}
        cancelTestId="dialog-cerrar-sesion-cancelar-button"
        confirmTestId="dialog-cerrar-sesion-confirmar-button"
      />
    </>
  );
};

/** Sidebar de escritorio: panel fijo a la izquierda. En móvil lo reemplaza `NavbarTofuMobile`. */
export const TofuSidebar = () => (
  <Box
    sx={{
      width: SIDEBAR_WIDTH,
      flex: 1,
      flexShrink: 0,
      display: { xs: "none", md: "flex" },
      flexDirection: "column",
      bgcolor: (theme) => getSidebarColors(theme).bg,
      borderRight: "1px solid",
      borderColor: (theme) => getSidebarColors(theme).bg,
    }}
  >
    <TofuSidebarContent />
  </Box>
);
